import { Badge, Button } from '../components/ui.jsx';

/**
 * HowItWorks — explains the loan-against-mutual-funds model.
 *
 * Included because the product is genuinely unfamiliar: a shopper who does not
 * understand that units are pledged rather than sold cannot evaluate the offer.
 * The page is static content, so it does no data fetching.
 */

const STEPS = [
  {
    title: 'Pick a phone and a plan',
    body: 'Choose your variant, then compare every tenure side by side. Each plan shows the monthly instalment, the interest rate, any cashback and the total you will repay.',
  },
  {
    title: 'Pledge units, do not sell them',
    body: 'Instead of redeeming investments, you lien-mark mutual fund units as collateral. The lender advances a percentage of their value — the loan-to-value, or LTV.',
  },
  {
    title: 'Your units stay invested',
    body: 'Pledged units are frozen for transacting but remain yours and remain in the market. Any growth over the tenure is still yours; so is any fall.',
  },
  {
    title: 'Repay monthly, then unpledge',
    body: 'Pay the instalment each month. When the final payment clears, the lien is released and your units are fully liquid again.',
  },
];

const FAQS = [
  {
    q: 'Why is the interest rate lower than a personal loan?',
    a: 'Because the loan is secured. Your pledged units reduce the lender’s risk, so the rate is well below unsecured options like a credit card revolve or a personal loan.',
  },
  {
    q: 'How much do I need to pledge?',
    a: 'It depends on the fund type. Equity funds are more volatile so lenders typically advance around 50% of value, meaning you pledge roughly twice what you borrow. Debt funds are steadier and can support 75–85%.',
  },
  {
    q: 'What is a no-cost EMI really?',
    a: 'A plan where the instalments add up to exactly the purchase price — no interest and no processing fee. On this site, any plan labelled "no cost" is verified in code to total the price precisely.',
  },
  {
    q: 'What happens if the market falls?',
    a: 'If the value of pledged units drops far enough, the loan can breach its LTV limit and the lender may ask you to pledge more units or repay part of the balance. This is the main risk of borrowing against investments.',
  },
  {
    q: 'Are the projected growth figures guaranteed?',
    a: 'No. Growth projections on this site are illustrations at a fixed assumed rate, shown to explain what staying invested could be worth. Actual market returns vary and can be negative.',
  },
];

export default function HowItWorks() {
  return (
    <div className="container-page py-14">
      <div className="max-w-3xl">
        <Badge tone="brand">The model</Badge>
        <h1 className="mt-4 text-4xl font-bold tracking-tight text-ink-950 sm:text-5xl">
          Borrow against your funds, not against your future
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-ink-600">
          Selling investments to fund a purchase means giving up every rupee of growth they would have earned. A
          loan against mutual funds avoids that: you keep the units and borrow against them instead.
        </p>
      </div>

      {/* Steps */}
      <ol className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((step, index) => (
          <li key={step.title} className="surface p-5">
            <span className="grid size-9 place-items-center rounded-xl bg-brand-600 font-bold text-white">
              {index + 1}
            </span>
            <h2 className="mt-4 font-semibold leading-snug text-ink-950">{step.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-600">{step.body}</p>
          </li>
        ))}
      </ol>

      {/* Comparison */}
      <section className="mt-16" aria-labelledby="compare-heading">
        <h2 id="compare-heading" className="text-2xl font-bold tracking-tight text-ink-950">
          How it compares
        </h2>
        <div className="mt-5 overflow-x-auto rounded-card border border-ink-200/70">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Approach</th>
                <th scope="col" className="px-4 py-3 font-semibold">Typical cost</th>
                <th scope="col" className="px-4 py-3 font-semibold">Investments kept</th>
                <th scope="col" className="px-4 py-3 font-semibold">Main drawback</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200/70 bg-white">
              <tr className="bg-growth-50/50">
                <th scope="row" className="px-4 py-3.5 text-left font-semibold text-ink-950">
                  Pledge mutual funds
                </th>
                <td className="px-4 py-3.5 text-ink-700">0–12.5% p.a.</td>
                <td className="px-4 py-3.5 font-semibold text-growth-700">Yes</td>
                <td className="px-4 py-3.5 text-ink-600">LTV can be breached if markets fall sharply</td>
              </tr>
              <tr>
                <th scope="row" className="px-4 py-3.5 text-left font-semibold text-ink-950">
                  Redeem investments
                </th>
                <td className="px-4 py-3.5 text-ink-700">No interest, but exit load and capital gains tax</td>
                <td className="px-4 py-3.5 text-ink-600">No</td>
                <td className="px-4 py-3.5 text-ink-600">All future compounding on that amount is lost</td>
              </tr>
              <tr>
                <th scope="row" className="px-4 py-3.5 text-left font-semibold text-ink-950">
                  Personal loan
                </th>
                <td className="px-4 py-3.5 text-ink-700">Around 16% p.a.</td>
                <td className="px-4 py-3.5 text-ink-600">Yes</td>
                <td className="px-4 py-3.5 text-ink-600">Unsecured, so materially more expensive</td>
              </tr>
              <tr>
                <th scope="row" className="px-4 py-3.5 text-left font-semibold text-ink-950">
                  Credit card revolve
                </th>
                <td className="px-4 py-3.5 text-ink-700">Up to 42% p.a.</td>
                <td className="px-4 py-3.5 text-ink-600">Yes</td>
                <td className="px-4 py-3.5 text-ink-600">By far the highest cost of the four</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-16" aria-labelledby="faq-heading">
        <h2 id="faq-heading" className="text-2xl font-bold tracking-tight text-ink-950">
          Common questions
        </h2>
        <div className="mt-5 divide-y divide-ink-200/70 overflow-hidden rounded-card border border-ink-200/70 bg-white">
          {FAQS.map((faq) => (
            <details key={faq.q} className="group">
              <summary className="flex cursor-pointer items-center justify-between gap-4 px-5 py-4 font-semibold text-ink-900 hover:bg-ink-50">
                {faq.q}
                <span className="shrink-0 text-ink-400 transition-transform group-open:rotate-45" aria-hidden="true">
                  <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                  </svg>
                </span>
              </summary>
              <p className="px-5 pb-5 text-sm leading-relaxed text-ink-600">{faq.a}</p>
            </details>
          ))}
        </div>
      </section>

      <div className="mt-14 flex flex-wrap gap-3">
        <Button to="/" size="lg">
          Browse phones
        </Button>
      </div>
    </div>
  );
}
